import { useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";

import { useGetVehicleOptionsQuery } from "api/memberPortal/contractVehicle/contractVehicle.api";
import { useGetOrgOptionsByOrgTypeQuery } from "api/memberPortal/orgs/orgs.api";
import { VALIDATOR_ROLES } from "api/memberPortal/project/personnel/personnel.types";
import {
  PRODUCT_STATUSES,
  ALLOWED_STATUSES_REPORT,
} from "api/memberPortal/project/product/productStatuses.type";
import { useExpandedEcrAssignmentParticipatingOrgsQuery } from "api/memberPortal/reports/expandedEcrAssignment/expandedEcrAssignment.api";
import { TECH_TYPE_OPTIONS_ARRAY } from "common/constants/techTypes";
import ReactSelect from "components/ReactSelect";

const LABS = [];
const VALIDATOR_ORGS = [];
const CONTRACT_VEHICLES = [];

export default function Filter({ filters, setFilters }) {
  const [currentFilters, setCurrentFilters] = useState({
    ...filters,
    status: filters?.status?.split(","),
  });

  const { data: labs = LABS } = useGetOrgOptionsByOrgTypeQuery("Lab");
  const { data: validatorOrgs = VALIDATOR_ORGS } =
    useExpandedEcrAssignmentParticipatingOrgsQuery();
  const { data: contractVehicles = CONTRACT_VEHICLES } =
    useGetVehicleOptionsQuery();

  const [formKey, setFormKey] = useState(Date.now());

  const handleChange = (event) => {
    if (event.target.name === "status") {
      let filter = currentFilters[event.target.name];
      if (!filter) {
        filter = [event.target.value];
      } else {
        if (event.target.checked) {
          filter.push(event.target.value);
        } else {
          const idx = filter.indexOf(event.target.value);
          filter.splice(idx, 1);
        }
      }
      setCurrentFilters({ ...currentFilters, [event.target.name]: filter });
    } else if (event.target.value.length === 0) {
      const newFilters = currentFilters;
      delete newFilters[event.target.name];
      setCurrentFilters({ ...newFilters });
    } else {
      setCurrentFilters({
        ...currentFilters,
        [event.target.name]: event.target.value,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFilters({
      ...currentFilters,
      status: currentFilters?.status?.join(","),
    });
  };

  const handleClear = () => {
    setFilters({});
    setCurrentFilters({
      status: Object.values(ALLOWED_STATUSES_REPORT),
    });
    setFormKey(Date.now());
  };

  const validatorRoleOptions = Object.values(VALIDATOR_ROLES).map((role) => ({
    label: role,
    value: role,
  }));

  const techTypeOptions = Object.values(TECH_TYPE_OPTIONS_ARRAY).map(
    (type) => ({
      label: type,
      value: type,
    }),
  );

  return (
    <Form onSubmit={handleSubmit} id="filters" key={formKey}>
      <Form.Group className="d-inline-block" controlId="from-date">
        <Form.Label>Time Frame</Form.Label>
        <Form.Control type="date" name="from_date" onChange={handleChange} />
      </Form.Group>
      <p className="d-inline-block p-3">to</p>
      <Form.Group className="d-inline-block mb-3" controlId="to-date">
        <Form.Label>Time Frame</Form.Label>
        <Form.Control type="date" name="to_date" onChange={handleChange} />
      </Form.Group>
      <Form.Group controlId="date-type" className="mb-3">
        <Form.Label>For The</Form.Label>
        <Form.Select name="date_type" onChange={handleChange}>
          <option value="">Select a type for the time frame</option>
          <option value="created_on">Entry Date</option>
          <option value="edited_on">Modification Date</option>
          <option value="est_completion_date">Estimated Completion Date</option>
          {/* <option value="due">Projected Start Date</option> */}
          <option value="kicked_off_date">Kickoff Date</option>
        </Form.Select>
      </Form.Group>
      <Form.Group controlId="validator_first_name" className="mb-3">
        <Form.Label>Team Member’s First Name:</Form.Label>
        <Form.Control
          type="text"
          name="validator_first_name"
          onChange={handleChange}
        />
      </Form.Group>
      <Form.Group controlId="validator_last_name" className="mb-3">
        <Form.Label>Team Member’s Last Name:</Form.Label>
        <Form.Control
          type="text"
          name="validator_last_name"
          onChange={handleChange}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="validator_roles">
          Team Member&apos;s Role
        </Form.Label>
        <ReactSelect
          id="validator_roles"
          name="validator_roles"
          options={validatorRoleOptions}
          newObj={currentFilters}
          handleChange={handleChange}
          isMulti={true}
          isValid={true}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="validator_vehicles">
          Team Member&apos;s Contract Vehicle
        </Form.Label>
        <ReactSelect
          id="validator_vehicles"
          name="validator_vehicles"
          options={contractVehicles}
          newObj={currentFilters}
          handleChange={handleChange}
          isMulti={true}
          isValid={true}
        />
      </Form.Group>
      <Form.Group controlId="status" className="mb-3">
        <Form.Label>Status</Form.Label>
        <Row>
          {Object.values(ALLOWED_STATUSES_REPORT).map((status) => (
            <Col lg={6} xs={12} key={status}>
              <Form.Check
                type="checkbox"
                name="status"
                value={status}
                label={
                  status === PRODUCT_STATUSES.CERTIFIED ? "Completed" : status
                }
                onChange={handleChange}
                defaultChecked={currentFilters.status?.some(
                  (s) => s === status,
                )}
                id={`status-${status}`}
              />
            </Col>
          ))}
        </Row>
      </Form.Group>
      <Form.Group controlId="tech_type" className="mb-3">
        <Form.Label>Tech Types:</Form.Label>
        <ReactSelect
          name="tech_type"
          handleChange={handleChange}
          defaultValue={techTypeOptions?.filter((type) =>
            currentFilters?.tech_type?.includes(type.value),
          )}
          options={techTypeOptions}
          newObj={currentFilters}
          isValid={true}
          isMulti={true}
        />
      </Form.Group>
      <Form.Group controlId="validator_org" className="mb-3">
        <Form.Label>Participating Organizations:</Form.Label>
        <ReactSelect
          name="validator_org"
          handleChange={handleChange}
          defaultValue={validatorOrgs?.filter((org) =>
            currentFilters?.validator_org?.includes(org.value),
          )}
          options={validatorOrgs}
          newObj={currentFilters}
          isMulti={true}
          isValid={true}
        />
      </Form.Group>
      <Form.Group controlId="associated_labs" className="mb-3">
        <Form.Label>Labs:</Form.Label>
        <ReactSelect
          name="associated_labs"
          handleChange={handleChange}
          defaultValue={labs?.filter((lab) =>
            currentFilters?.associated_labs?.includes(lab.value),
          )}
          options={labs}
          newObj={currentFilters}
          isMulti={true}
          isValid={true}
        />
      </Form.Group>
      <div className="mb-3">
        <Button type="submit" variant="primary" onClick={handleSubmit}>
          Apply
        </Button>
        &nbsp;
        <Button type="reset" variant="outline-dark" onClick={handleClear}>
          Clear
        </Button>
      </div>
    </Form>
  );
}
